import {
  CSSProperties,
  FC,
  PointerEventHandler,
  MouseEventHandler,
  ReactNode,
  HTMLAttributes,
  ComponentProps,
  Ref,
  forwardRef,
  useRef,
  useState,
} from 'react';
import {
  autoPlacement,
  autoUpdate,
  FloatingPortal,
  shift,
  size,
  offset,
  flip,
  useFloating,
  FloatingArrow,
  arrow,
  Placement,
  useHover,
  useInteractions,
  useClick,
  useDismiss,
  useMergeRefs,
} from '@floating-ui/react';
import styled from 'styled-components';
import {
  borderRadius,
  boxShadowSecondary,
  colorText,
} from '@atlas-frontend/design-tokens';

const TOOLTIP_OFFSET_FROM_ORIGIN = 15;
const TOOLTIP_OFFSET_FROM_VIEWPORT = 8;
const TOOLTIP_MAX_WIDTH = 600;
const TOOLTIP_MAX_HEIGHT = 400;
const TOOLTIP_BACKGROUND_COLOR_DARK = 'rgba(0, 0, 0, 0.85)';
const TOOLTIP_BACKGROUND_COLOR_LIGHT = 'white';
const TOOLTIP_ARROW_DROP_SHADOW_FILTER = boxShadowSecondary
  .map((value) => `drop-shadow(${value})`)
  .join(' ');

type WrapperType =
  | 'span'
  | 'div'
  | FC<{
      style?: CSSProperties;
      onPointerDown?: PointerEventHandler;
      onPointerEnter?: PointerEventHandler;
      onMouseMove?: MouseEventHandler;
      children: ReactNode;
    }>;

type WrapperProps<W extends WrapperType> = W extends 'div' | 'span'
  ? HTMLAttributes<HTMLElementTagNameMap[W]>
  : ComponentProps<W>;

export type TooltipPropsBase<W extends WrapperType> = Omit<
  WrapperProps<W>,
  'title'
> & {
  title: ReactNode;
  placement?: Placement | 'auto';
  wrapperType?: W;
  trigger?: 'hover' | 'click';
  variant?: 'dark' | 'light';
  stopClickPropagation?: boolean;
  disabled?: boolean;
};

type TooltipProps<W extends WrapperType> = TooltipPropsBase<W> &
  (
    | {
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
      }
    | {
        isOpen?: never;
        onOpenChange?: never;
      }
  );

export const Tooltip = <W extends WrapperType>(props: TooltipProps<W>) => {
  if (
    !props.title ||
    (Array.isArray(props.title) &&
      (props.title.length === 0 || props.title.every((t) => !t)))
  ) {
    return props.children;
  }

  return <TooltipInner {...props} />;
};

const TooltipInner = forwardRef(function TooltipInner<W extends WrapperType>(
  props: TooltipProps<W>,
  forwardedRef: Ref<Element>,
) {
  const {
    title,
    children,
    placement = 'auto',
    wrapperType: Wrapper = 'span' as WrapperType,
    trigger = 'hover',
    variant = 'dark',
    style,
    stopClickPropagation = false,
    disabled = false,
    isOpen: isOpenProp,
    onOpenChange,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const isControlled = typeof isOpenProp === 'boolean';
  const open = !disabled && (isControlled ? isOpenProp : isOpen);

  const middleware = [
    placement === 'auto' ? autoPlacement() : null,
    placement === 'auto' ? null : flip(),
    shift({ padding: TOOLTIP_OFFSET_FROM_VIEWPORT }),
    offset(TOOLTIP_OFFSET_FROM_ORIGIN),
    arrow({ element: arrowRef }),
    size({
      apply(state) {
        const { availableWidth, availableHeight, elements } = state;
        elements.floating.style.maxWidth = `${
          availableWidth - TOOLTIP_OFFSET_FROM_VIEWPORT * 2
        }px`;
        elements.floating.style.setProperty(
          '--atlas-tooltip-available-height',
          `${Math.max(0, availableHeight - TOOLTIP_OFFSET_FROM_VIEWPORT)}px`,
        );
      },
    }),
  ].filter(Boolean);

  const { refs, floatingStyles, context } = useFloating({
    ...(isControlled
      ? { open, onOpenChange }
      : { open, onOpenChange: setIsOpen }),
    strategy: 'fixed',
    placement: placement === 'auto' ? undefined : placement,
    middleware: middleware as any,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    // without a delay the user can never move the mouse over the tooltip itself
    // to interact with it (e.g. scroll the overflowing content in there).
    delay: 50,
    enabled: !disabled && trigger === 'hover',
  });
  const click = useClick(context, {
    enabled: !disabled && trigger === 'click',
  });
  const dismiss = useDismiss(context, {
    enabled: !disabled && trigger === 'click',
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
  ]);

  const referenceRef = useMergeRefs([refs.setReference, forwardedRef]);

  return (
    <>
      <Wrapper
        {...(rest as any)}
        ref={referenceRef}
        {...getReferenceProps({
          style: { minWidth: 0 },
          onClick: stopClickPropagation
            ? (event) => event.stopPropagation()
            : undefined,
        })}
      >
        {children}
      </Wrapper>

      {open && (
        <FloatingPortal>
          <StyledTooltip
            ref={refs.setFloating}
            {...getFloatingProps({ style: floatingStyles, role: 'tooltip' })}
          >
            <StyledTooltipContent $variant={variant} style={style}>
              {title}
            </StyledTooltipContent>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={
                variant === 'dark'
                  ? TOOLTIP_BACKGROUND_COLOR_DARK
                  : TOOLTIP_BACKGROUND_COLOR_LIGHT
              }
              style={
                variant === 'light'
                  ? { filter: TOOLTIP_ARROW_DROP_SHADOW_FILTER }
                  : undefined
              }
            />
          </StyledTooltip>
        </FloatingPortal>
      )}
    </>
  );
});

const StyledTooltip = styled.div`
  z-index: 1001;
`;

export const StyledTooltipContent = styled.div<{ $variant: 'dark' | 'light' }>`
  --atlas-tooltip-max-height: ${TOOLTIP_MAX_HEIGHT}px;
  ${({ $variant }) =>
    $variant === 'dark'
      ? `
  background-color: ${TOOLTIP_BACKGROUND_COLOR_DARK};
  color: white;

  a,
  .ant-btn-color-link {
    color: inherit !important;
  }
  `
      : `
  background-color: ${TOOLTIP_BACKGROUND_COLOR_LIGHT};
  color: ${colorText};
  box-shadow: ${boxShadowSecondary.join(', ')};
  `}
  padding: 6px 8px;
  border-radius: ${borderRadius}px;
  max-width: ${TOOLTIP_MAX_WIDTH}px;
  max-height: ${TOOLTIP_MAX_HEIGHT}px;
  overflow: auto;
`;
